// Frame scheduling: coalesced draws, coarse strides while interacting, and a
// progressive full-detail pass for renderers that need one (Canvas 2D).
import type { Detail } from '../types.ts';
import type { FrameBudget, FrameParams, Renderer, RenderStats } from './renderer.ts';

export function strideFor(detail: Detail, nPoints: number, budget: FrameBudget, forInteraction: boolean): number {
  if (detail !== 'auto') return Math.max(1, detail | 0);
  const b = forInteraction ? budget.interact : budget.settled;
  if (!isFinite(b)) return 1;
  return Math.max(1, Math.ceil(nPoints / b));
}

export interface LoopDeps {
  renderer: () => Renderer;
  frameParams: (stride: number) => FrameParams;
  nPoints: () => number;
  detail: () => Detail;
  onFrame: (stats: RenderStats, ms: number, refining: boolean) => void;
}

export class RenderLoop {
  interacting = false;
  lastDrawMs = 0;
  lastStats: RenderStats | null = null;
  refinedStride = 0;
  private drawPending = false;
  private settleTimer = 0;
  private refining = false;

  constructor(private deps: LoopDeps) {}

  requestDraw(): void {
    if (this.drawPending) return;
    this.drawPending = true;
    requestAnimationFrame(() => { this.drawPending = false; this.draw(); });
  }

  settleSoon(ms: number): void {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => {
      this.settleTimer = 0;
      this.interacting = false;
      this.requestDraw();
    }, ms);
  }

  /** While the pointer is panning/zooming, draw a coarser sample. */
  markInteracting(): void {
    this.interacting = true;
    this.deps.renderer().cancelRefine?.();
    this.settleSoon(260);
  }

  /** Deterministic escape hatch: redraw every vertex right now. */
  forceFullDetail(): void {
    if (this.settleTimer) { clearTimeout(this.settleTimer); this.settleTimer = 0; }
    this.interacting = false;
    this.requestDraw();
  }

  private draw(): void {
    const r = this.deps.renderer();
    r.cancelRefine?.();
    this.refining = false;
    this.refinedStride = 0;
    const n = this.deps.nPoints();
    const detail = this.deps.detail();
    const fullStride = strideFor(detail, n, r.budget, false);
    const coarseStride = strideFor(detail, n, r.budget, true);
    // Canvas: a settled full-detail pass this large blocks the main thread.
    // Put a coarse frame up immediately and refine into it.
    const canRefine = !!r.refineProgressive;
    const heavy = canRefine && n / fullStride > 150000;
    const stride = this.interacting
      ? coarseStride
      : heavy ? Math.max(fullStride, coarseStride) : fullStride;

    const t0 = performance.now();
    const stats = r.draw(this.deps.frameParams(stride));
    this.lastDrawMs = Math.round(performance.now() - t0);
    this.lastStats = stats;

    if (!this.interacting && stride > fullStride && r.refineProgressive) {
      this.refining = true;
      const t1 = performance.now();
      r.refineProgressive(this.deps.frameParams(fullStride), 16, () => {
        this.refining = false;
        this.refinedStride = fullStride;
        this.lastDrawMs = Math.round(performance.now() - t1);
        this.deps.onFrame(stats, this.lastDrawMs, false);
      });
    }
    this.deps.onFrame(stats, this.lastDrawMs, this.refining);
  }
}
